import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import Card from '@/shared/Card'

import { useGlobalState } from '@/state'
import useModal from '@/hooks/useModalV2'
import Loading from '@/shared/Loading'
import Modal from '@/shared/Modal'
import { XCircleIcon } from '@heroicons/react/solid'
import useQuery from '@/hooks/useQuery'


import DemoForm from '@/pages/Users/ProfileDemos/DemoForm'
import DemoRow from '@/pages/Users/ProfileDemos/DemoRow'

const ShowUpgradePanel = () => {
  return <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">One demo on the free plan</h3>
    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Every paid plan lifts the cap. Unlimited demos, and it keeps the lights on around here.</p>
    <Link to='/membership' className="mt-4 inline-flex items-center rounded-md bg-cccblue px-4 py-2 text-sm font-medium text-white hover:bg-cccblue-alt hover:text-white">See the plans</Link>
  </div>
}

const ProfileDemosIndex = () => {
  const { getRequest, putpostRequest } = useQuery()
  const { openModal, closeModal, isOpen, willClose } = useModal()

  const [usersContainer, setUsersContainer] = useGlobalState('usersContainer')
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const [loading, setLoading] = useState(true)
  const [mounted, setMounted] = useState(false)
  const { demos } = usersContainer

  // Optimistically swap two adjacent demos, fire the bulk reorder
  // POST, roll back on error. Backend accepts the new top-to-bottom
  // order as an array of ids; only ids the viewer owns are honored.
  const moveDemo = (idx, delta) => {
    const target = idx + delta
    if (target < 0 || target >= demos.length) { return }
    const next = demos.slice()
    const [moved] = next.splice(idx, 1)
    next.splice(target, 0, moved)
    const prev = demos
    setUsersContainer({ ...usersContainer, demos: next })
    putpostRequest('/api/v3/demos/reorder', 'POST', { order: next.map(d => d.id) }, (err) => {
      if (err) {
        setUsersContainer({ ...usersContainer, demos: prev })
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-white text-gray-800">Couldn't reorder</p>
          <p className="mt-1 text-sm dark:text-gray-100 text-gray-500">Try again later.</p>
        </div>)
      }
    })
  }

  useEffect(() => { setMounted(true) }, [])
  useEffect(() => { fetchData() }, [mounted]) // not sure why, but: Can't perform a React state update on an unmounted component.

  useEffect(() => {
    if (!willClose) { return }

    fetchData()
  }, [willClose])

  const fetchData = () => {
    if (!mounted) { return }

    setLoading(true)
    getRequest('/api/v3/demos', {}, (err, jsonData) => {
      setLoading(false)
      if (err) { /* hooks */ return }

      setUsersContainer({
        ...usersContainer,
        demos: jsonData.demos
      })
    })
  }

  if (loading) {
    return (
      <section aria-labelledby="loading" className='p-5'>
        <button type="button" className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" >
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300">
            Demos
            <Loading />
          </span>
        </button>
      </section>
    )
  }

  const canAddMore = demos.length < 1 || currentUser.multipleDemos

  return <>
    <Modal full={canAddMore} isOpen={isOpen} closeModal={closeModal} >
      <div className='relative mb-1'>
        <span className="sr-only" hidden>Close Modal</span>
        <XCircleIcon onClick={closeModal} className='cursor-pointer w-7 h-7 absolute -top-5 -right-5 text-gray-500 dark:text-gray-400' />
      </div>
      { !canAddMore && <ShowUpgradePanel /> }
      { canAddMore && <DemoForm demo={{}} setDemo={() => {}} /> }
    </Modal>
    <div>
      <div className="md:grid md:grid-cols-4 md:gap-6 space-x-2">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Demos and samples</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Reels, clips, scripts, portfolios. The first thing a producer opens. You can also attach these to auditions.</p>
          </div>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-3">
          { demos.length === 0 && <>
            <section aria-labelledby="my-course-content" onClick={openModal}>
              <button type="button" className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" >
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300">No demos yet. Add a reel, a YouTube link, a script, a portfolio. Anything that shows what you can do.</span>
              </button>
            </section>
          </> }
          { demos.length > 0 && <>
            <Card header={<button type='button' className='text-sm font-semibold text-cccblue hover:text-cccblue-alt' onClick={openModal}>+ Add a demo</button>}>
              <div className="grid grid-cols-3 gap-6">
                <div className='col-span-3'>
                  <div className="sm:rounded-md">
                    <ul className="space-y-3">
                      { demos.map((demo, idx) => (
                        <DemoRow
                          key={`demo${demo.id}`}
                          demo={demo}
                          fetchData={fetchData}
                          canMoveUp={idx > 0}
                          canMoveDown={idx < demos.length - 1}
                          onMoveUp={() => moveDemo(idx, -1)}
                          onMoveDown={() => moveDemo(idx, 1)}
                        />
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </> }
        </div>
      </div>
    </div>
  </>
}

export default ProfileDemosIndex
